import { Box, Heading } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import Markdown, { Components, ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";

import { objectFromEntries } from "@nens/shared-utils";

const headingTypes = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;

const createHeadingComponents = (): Partial<Components> =>
  objectFromEntries(
    headingTypes.map((type) => [
      type,
      (
        props: React.ClassAttributes<HTMLHeadingElement> &
          React.HTMLAttributes<HTMLHeadingElement> &
          ExtraProps
      ) => <Heading size={type} as={type} {...props} />,
    ])
  );

const NO_MARGIN_CLASS_NAME = "no-top-margin";

export function TbiMarkdown({ markdown }: { markdown: string | null | undefined }) {
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Make sure the first child doesn't have a top margin (just using :first-child gives an error even though it works)
  useEffect(() => {
    if (!contentRef.current) return;
    const children = contentRef.current.querySelectorAll("*");
    if (!children.length) return;
    children.forEach((element) => element.classList.remove(NO_MARGIN_CLASS_NAME));
    children[0].classList.add(NO_MARGIN_CLASS_NAME);
  }, [markdown]);

  return (
    <Box
      ref={contentRef}
      sx={{
        "p, h1, h2, h3, h4, h5, h6, hr": { marginTop: "12px !important" },
        [`.${NO_MARGIN_CLASS_NAME}`]: {
          marginTop: "0 !important",
        },

        "ul, ol": { paddingLeft: "1.5em" },

        th: { textAlign: "start" },
      }}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          /* Open links in new tab */
          a: ({ children, ...props }) => (
            <a target="_blank" style={{ textDecoration: "underline" }} {...props}>
              {children}
            </a>
          ),
          ...createHeadingComponents(),
        }}
      >
        {markdown}
      </Markdown>
    </Box>
  );
}
